/**
 * Pure decisions behind embedded-app store initialisation.
 *
 * Extracted so they can be tested without a DOM, an iframe or a network —
 * the same reason lib/tokenRecovery.js exists. The effects that use them live
 * in context/AppContext.js.
 */

/**
 * Which of the stores the backend returned this boot is about.
 *
 * `/stores` answers with the caller's own store only, so this is normally a
 * list of one; the shape is kept because the selection rules below still have
 * to hold for a standalone (non-embedded) session that can see several.
 *
 * @param {Array}  stores           What GET /stores returned.
 * @param {object} params           URL parameters from the embedded boot.
 * @param {string} [params.shop]    ?shop= — which shop Shopify launched us for.
 * @param {string} [params.storeId] ?store_id= — an explicit store to open.
 * @returns {object|null} The store to use, or null when none matches.
 */
export function pickActiveStore(stores, { shop, storeId } = {}) {
  const list = Array.isArray(stores) ? stores : [];
  if (!list.length) return null;

  if (storeId) return list.find(s => s?.id === storeId) || null;
  if (shop) return list.find(s => s?.shop_domain === shop) || null;

  // No URL context: prefer a real store that can actually talk to Shopify.
  const real = list.filter(s => s?.shop_domain && !s.shop_domain.includes('demo'));
  return real.find(s => s.has_access_token) || real[0] || list[0] || null;
}

/**
 * Whether a session-token exchange is worth attempting at all.
 *
 * Only inside Shopify Admin, where App Bridge exists to mint the token the
 * exchange consumes. Asking anywhere else is a guaranteed rejection on every
 * boot of the standalone app.
 *
 * @param {object} appBridge `window.shopify`, or undefined.
 * @param {object|null} activeStore The store already resolved, if any.
 * @returns {boolean}
 */
export function shouldBootstrap(appBridge, activeStore) {
  if (activeStore) return false;            // nothing to bootstrap
  return Boolean(appBridge && appBridge.idToken);
}

/**
 * The store to adopt after a bootstrap attempt.
 *
 * `recovered` means Shopify minted the offline access token. `bootstrapped`
 * means the record now exists even if the exchange itself did not complete —
 * still worth adopting, because it gets the app past initialisation and into
 * a state the merchant can act on rather than a dead one.
 *
 * @param {object|null} response  Body of POST /auth/session-token-exchange.
 * @param {Array} storesAfter     What GET /stores returned afterwards.
 * @returns {object|null}
 */
export function resolveBootstrappedStore(response, storesAfter) {
  if (!response || (!response.recovered && !response.bootstrapped)) return null;
  const list = Array.isArray(storesAfter) ? storesAfter : [];
  if (!list.length) return null;
  if (response.store_id) return list.find(s => s?.id === response.store_id) || list[0];
  return list[0];
}
